import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Square, ArrowLeft, Loader2, Volume2, Captions } from 'lucide-react';
import { linkProps } from '../lib/router';
import { STATUS, useVoiceAsk } from '../hooks/useVoiceAsk';
import { fetchSession } from '../lib/askApi';

/**
 * Deliberately monochrome, to match the homepage: black ground, white text,
 * light weights, zinc for anything secondary. No accent colour and no theme
 * toggle, because the site is dark-only.
 *
 * This page is self-contained. It shares no components with App.js, which
 * keeps the module that decides which page to render from being imported by
 * one of the pages it renders.
 */

const FALLBACK_LANGUAGES = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'nl', label: 'Dutch', native: 'Nederlands' },
  { code: 'de', label: 'German', native: 'Deutsch' },
  { code: 'fr', label: 'French', native: 'Français' },
  { code: 'es', label: 'Spanish', native: 'Español' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
];

const PROMPTS = {
  [STATUS.idle]: 'Tap to ask a question',
  [STATUS.listening]: 'Listening… tap to stop',
  [STATUS.thinking]: 'Thinking…',
  [STATUS.speaking]: 'Answering…',
};

const ERROR_MESSAGES = {
  permission: 'I need microphone access to hear your question.',
  tooShort: 'That was too short. Tap and speak.',
  tooLong: 'That was too long. Keep questions under 25 seconds.',
  rateLimited: null, // the server's own wording is better here
  busy: 'Busy right now. Try again in a moment.',
  network: 'Could not reach the server. Try again shortly.',
  playback: 'Your device blocked the audio. The answer is below.',
  noAudio: 'The voice is unavailable right now. The answer is below.',
  session: 'That took too long. Please try again.',
};

export default function Dissertation() {
  const [language, setLanguage] = useState('auto');
  const [languages, setLanguages] = useState(FALLBACK_LANGUAGES);
  const [showText, setShowText] = useState(false);
  const [typed, setTyped] = useState('');

  const { status, level, error, answer, toggle, askText, supported } =
    useVoiceAsk({ language });

  // The language list comes from the server so the picker can never offer
  // something the backend has no voice for.
  useEffect(() => {
    const controller = new AbortController();
    fetchSession(controller.signal)
      .then((session) => {
        if (session.languages?.length) setLanguages(session.languages);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  // When the voice cannot be delivered, the text is all there is, so it stops
  // being optional.
  const mustShowText =
    Boolean(answer && !answer.spoken) ||
    error?.kind === 'playback' ||
    error?.kind === 'noAudio';

  const visibleText = showText || mustShowText ? answer?.text : null;
  const errorMessage = error ? ERROR_MESSAGES[error.kind] ?? error.message : null;

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <a
          {...linkProps('/')}
          className="inline-flex items-center gap-2 text-sm font-medium text-white/50 hover:text-white transition-colors tracking-widest uppercase"
        >
          <ArrowLeft size={14} />
          Back
        </a>
      </header>

      <main className="flex flex-col items-center px-6 pt-16 pb-20 text-center">
        <motion.h1
          className="text-3xl md:text-5xl font-light text-white"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Ask about my dissertation
        </motion.h1>
        <motion.p
          className="mt-5 max-w-md text-sm md:text-base font-light leading-relaxed text-zinc-400"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
        >
          Speak a question in any language about my PhD on occupant comfort in
          smart buildings. You will hear the answer back in the same language.
        </motion.p>

        <LanguagePicker
          value={language}
          onChange={setLanguage}
          languages={languages}
          disabled={status !== STATUS.idle}
        />

        {supported ? (
          <>
            <TalkButton status={status} level={level} onClick={toggle} />
            <p
              className="mt-10 h-6 text-sm font-light tracking-wide text-zinc-500"
              aria-live="polite"
            >
              {PROMPTS[status]}
            </p>
          </>
        ) : (
          <TypedFallback
            value={typed}
            onChange={setTyped}
            onSubmit={() => {
              askText(typed);
              setTyped('');
            }}
            busy={status === STATUS.thinking}
          />
        )}

        <AnimatePresence>
          {errorMessage && (
            <motion.p
              key={errorMessage}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-3 max-w-sm text-sm font-light text-amber-400/80"
            >
              {errorMessage}
              {error?.retryAfter ? ` Try again in ${formatWait(error.retryAfter)}.` : ''}
            </motion.p>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {visibleText && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-10 max-w-xl rounded-lg border border-white/10 bg-white/5 p-6 text-left"
            >
              <p className="text-sm md:text-base font-light leading-relaxed text-zinc-300">
                {visibleText}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {answer?.text && !mustShowText && (
          <button
            type="button"
            onClick={() => setShowText((current) => !current)}
            className="mt-8 inline-flex items-center gap-2 text-xs tracking-widest uppercase text-zinc-600 hover:text-white transition-colors"
          >
            <Captions size={13} />
            {showText ? 'Hide text' : 'Show text'}
          </button>
        )}

        <Summary />
      </main>
    </div>
  );
}

/**
 * A plain-language account of the thesis, on the page itself.
 *
 * Someone who has just arrived does not know enough to ask a good question
 * yet. Four short paragraphs give them the shape of the work, and the
 * vocabulary to ask about the part they care about.
 */
function Summary() {
  return (
    <section className="mt-24 w-full max-w-xl border-t border-white/10 pt-12 text-left">
      <h2 className="text-xs tracking-widest uppercase text-zinc-600">
        In plain words
      </h2>
      <div className="mt-6 space-y-5 text-sm md:text-[15px] font-light leading-relaxed text-zinc-400">
        <p>
          Buildings use a large share of the world's energy, most of it on
          heating and cooling. Even so, a great many people are uncomfortable
          at work, and that stays true in buildings that meet every official
          standard. Those standards describe an average person who does not
          exist.
        </p>
        <p>
          My research asks what changes if you run a building around what the
          people inside actually report, rather than around a formula. I built
          a phone and smartwatch app that let office workers say how they felt
          at that moment, and used their answers to build comfort models for
          one particular building. Those models predicted far better than the
          industry standard, and showed that what someone is wearing, and what
          sort of day they are having, matter more than is usually assumed.
        </p>
        <p>
          A survey across twelve Dutch offices then found something I did not
          expect. Satisfaction depends less on hitting a target temperature
          than on whether people feel they have some say over their
          surroundings, and whether they can understand what the building is
          doing and why.
        </p>
        <p>
          The last part is a working platform, ComfortOS, that carries this
          feedback back into how a building is actually run, so that what
          occupants report reaches the people and systems that can act on it.
        </p>
      </div>
      <p className="mt-8 text-xs font-light text-zinc-600">
        Ask the button above for any of this in more detail, in your own
        language.
      </p>
    </section>
  );
}

function formatWait(seconds) {
  if (seconds < 60) return `${seconds} seconds`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'}`;
}

function LanguagePicker({ value, onChange, languages, disabled }) {
  return (
    <div className="mt-10">
      <label htmlFor="answer-language" className="sr-only">
        Answer language
      </label>
      <select
        id="answer-language"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="appearance-none rounded-full border border-white/15 bg-white/5 px-4 py-2 pr-9 text-xs tracking-widest uppercase text-zinc-400 hover:text-white hover:border-white/30 focus:outline-none focus:ring-1 focus:ring-white/30 transition-colors disabled:opacity-40 cursor-pointer"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2371717a' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
          backgroundPosition: 'right 0.6rem center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '1.1em 1.1em',
        }}
      >
        <option value="auto" className="bg-black">Auto-detect</option>
        {languages.map((entry) => (
          <option key={entry.code} value={entry.code} className="bg-black">
            {entry.native}
          </option>
        ))}
      </select>
    </div>
  );
}

function TalkButton({ status, level, onClick }) {
  const listening = status === STATUS.listening;
  const thinking = status === STATUS.thinking;
  const speaking = status === STATUS.speaking;

  // The halo tracks the speaker's own voice while recording, so the control
  // visibly answers to them rather than animating on a timer.
  const glowScale = listening ? 1 + level * 0.4 : 1;

  return (
    <div className="relative mt-14 flex items-center justify-center">
      <motion.div
        aria-hidden
        className="absolute rounded-full bg-white blur-3xl"
        style={{ width: 200, height: 200, opacity: thinking ? 0.06 : 0.12 }}
        animate={{ scale: glowScale }}
        transition={{ type: 'spring', stiffness: 220, damping: 18 }}
      />

      {speaking && <Ripples />}

      <motion.button
        type="button"
        onClick={onClick}
        aria-label={PROMPTS[status]}
        className="relative z-10 flex h-32 w-32 md:h-36 md:w-36 items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur text-white hover:border-white/40 focus:outline-none focus:ring-1 focus:ring-white/40 transition-colors"
        whileTap={{ scale: 0.94 }}
        animate={thinking ? { scale: [1, 1.03, 1] } : { scale: 1 }}
        transition={
          thinking
            ? { repeat: Infinity, duration: 1.6, ease: 'easeInOut' }
            : { type: 'spring', stiffness: 300, damping: 20 }
        }
      >
        {thinking ? (
          <Loader2 size={34} className="animate-spin text-zinc-400" strokeWidth={1.5} />
        ) : listening ? (
          <Square size={30} className="text-white fill-current" />
        ) : speaking ? (
          <Volume2 size={36} strokeWidth={1.5} />
        ) : (
          <Mic size={40} strokeWidth={1.25} />
        )}
      </motion.button>
    </div>
  );
}

function Ripples() {
  return (
    <>
      {[0, 0.6, 1.2].map((delay) => (
        <motion.span
          key={delay}
          aria-hidden
          className="absolute rounded-full border border-white/20"
          style={{ width: 140, height: 140 }}
          initial={{ scale: 0.85, opacity: 0.5 }}
          animate={{ scale: 1.8, opacity: 0 }}
          transition={{ repeat: Infinity, duration: 1.8, delay, ease: 'easeOut' }}
        />
      ))}
    </>
  );
}

/**
 * Shown when the browser will not give up a microphone, which is common in the
 * in-app browsers that links opened from social apps run in. Without it, a
 * shared link is simply broken for a large share of visitors.
 */
function TypedFallback({ value, onChange, onSubmit, busy }) {
  const inputRef = useRef(null);

  return (
    <form
      className="mt-14 w-full max-w-md"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <p className="mb-4 text-sm font-light text-zinc-500">
        This browser will not share a microphone. Type your question instead.
      </p>
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          maxLength={400}
          onChange={(event) => onChange(event.target.value)}
          placeholder="What is your thesis about?"
          className="flex-1 rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm font-light text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white/30"
        />
        <button
          type="submit"
          disabled={busy || !value.trim()}
          className="rounded-lg border border-white/20 bg-white/10 px-5 py-3 text-xs tracking-widest uppercase text-white transition-colors hover:bg-white/20 disabled:opacity-30"
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : 'Ask'}
        </button>
      </div>
    </form>
  );
}
